/**
 * userAdaptation.js - User Adaptation Engine
 * Baba AI - Single Adaptive Model Core
 * 
 * Critical feature that enables ONE model to serve ALL users:
 * - Age detection/estimation
 * - Communication style matching
 * - Speed adaptation (turbo ↔ steady)
 * - Complexity level adjustment
 * - Full spectrum state detection (via stateDetection.js)
 * 
 * SPECTRUM COVERAGE:
 * - Neurotypical (casual → power user)
 * - ADHD (hyperactive, inattentive, combined)
 * - Autism spectrum
 * - Bipolar patterns
 * - Anxiety (cross-cutting)
 * - Safety monitoring
 * 
 * Philosophy: Don't ask, detect. Don't assume, clarify.
 * "Bedoel je dat?" when uncertain.
 * 
 * Privacy: ALL data stays LOCAL (localStorage only)
 * 
 * Updated: November 26, 2025 - Full spectrum integration
 */

// Import state detection for full spectrum analysis
import stateDetection from './stateDetection';

class UserAdaptation {
  constructor() {
    this.storageKey = 'baba-user-profile';
    this.profile = this.loadProfile();
    this.sessionObservations = [];
  }

  // ==========================================
  // PROFILE MANAGEMENT
  // ==========================================

  /**
   * Load user profile from localStorage
   */
  loadProfile() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[UserAdaptation] Could not load profile:', e);
    }

    // Default profile - neutral starting point
    return {
      // Demographics (estimated, not asked)
      estimatedAge: null,
      ageConfidence: 0,
      
      // Communication style
      style: {
        formality: 0.5,       // 0 = casual, 1 = formal
        verbosity: 0.5,       // 0 = terse, 1 = detailed
        technicality: 0.5,    // 0 = simple, 1 = technical
        directness: 0.5,      // 0 = gentle, 1 = blunt
        emoji: false,         // Uses emojis?
        profanity: false,     // Uses profanity?
      },
      
      // Speed & Pace
      pace: {
        messagesPerMinute: 0,
        averageMessageLength: 0,
        prefersTurbo: false,
        prefersDetailed: false,
      },
      
      // State patterns (ADHD foundation)
      states: {
        currentState: 'NORMAL',  // NORMAL, HIGH, LOW, ACTIVATED, BLOCKED
        stateHistory: [],
        lastStateChange: null,
      },
      
      // Language
      language: {
        primary: 'nl',        // Detected primary language
        codeSwitch: false,    // Switches between languages?
        languages: ['nl'],    // All detected languages
      },
      
      // Preferences (learned)
      preferences: {
        likesConfirmation: null,
        likesStructure: null,
        likesBulletPoints: null,
        likesEmoji: null,
      },
      
      // Meta
      totalMessages: 0,
      firstSeen: Date.now(),
      lastUpdated: Date.now(),
      confidenceLevel: 0,     // 0-1, how confident we are in profile
    };
  }

  /**
   * Save profile to localStorage
   */
  saveProfile() {
    try {
      this.profile.lastUpdated = Date.now();
      localStorage.setItem(this.storageKey, JSON.stringify(this.profile));
    } catch (e) {
      console.warn('[UserAdaptation] Could not save profile:', e);
    }
  }

  // ==========================================
  // MESSAGE ANALYSIS
  // ==========================================

  /**
   * Analyze incoming user message and update profile
   */
  analyzeMessage(message) {
    if (!message || typeof message !== 'string') return;

    this.profile.totalMessages++;
    
    // Record observation
    const observation = {
      timestamp: Date.now(),
      length: message.length,
      wordCount: message.split(/\s+/).length,
      hasEmoji: this.detectEmoji(message),
      hasProfanity: this.detectProfanity(message),
      language: this.detectLanguage(message),
      formality: this.estimateFormality(message),
      sentiment: this.estimateSentiment(message),
    };
    
    this.sessionObservations.push(observation);
    
    // Update profile based on observation
    this.updateStyleFromObservation(observation);
    this.updatePaceFromObservation(observation);
    this.updateLanguageFromObservation(observation);
    this.estimateAgeFromPatterns();
    this.detectStateFromPatterns();
    
    // NEW: Full spectrum state detection
    stateDetection.analyzeMessage(message, { timestamp: observation.timestamp });
    
    // Update confidence
    this.updateConfidence();
    
    // Save periodically (every 5 messages)
    if (this.profile.totalMessages % 5 === 0) {
      this.saveProfile();
    }
  }

  /**
   * Detect emoji usage
   */
  detectEmoji(text) {
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
    return emojiRegex.test(text);
  }

  /**
   * Detect profanity (Dutch + English)
   */
  detectProfanity(text) {
    const profanityWords = [
      'fuck', 'shit', 'damn', 'kut', 'godverdomme', 'klote', 'tering', 'kanker'
    ];
    const lower = text.toLowerCase();
    return profanityWords.some(word => lower.includes(word));
  }

  /**
   * Detect language
   */
  detectLanguage(text) {
    const lower = text.toLowerCase();
    
    // Dutch indicators
    const dutchWords = ['de', 'het', 'een', 'van', 'en', 'is', 'dat', 'niet', 'zijn', 'voor', 'maar', 'ook', 'dit', 'wat', 'kan', 'moet', 'wil'];
    const dutchCount = dutchWords.filter(w => lower.includes(` ${w} `) || lower.startsWith(`${w} `) || lower.endsWith(` ${w}`)).length;
    
    // English indicators
    const englishWords = ['the', 'is', 'and', 'to', 'of', 'that', 'for', 'with', 'this', 'not', 'but', 'can', 'what', 'should'];
    const englishCount = englishWords.filter(w => lower.includes(` ${w} `) || lower.startsWith(`${w} `) || lower.endsWith(` ${w}`)).length;
    
    // Farsi indicators (Persian)
    const farsiRegex = /[\u0600-\u06FF]/;
    const hasFarsi = farsiRegex.test(text);
    
    if (hasFarsi) return 'fa';
    if (dutchCount > englishCount) return 'nl';
    if (englishCount > dutchCount) return 'en';
    return 'nl'; // Default to Dutch
  }

  /**
   * Estimate formality level
   */
  estimateFormality(text) {
    let score = 0.5;
    
    // Formal indicators
    if (text.includes('Geachte') || text.includes('Met vriendelijke groet')) score += 0.3;
    if (text.includes('u ') || text.includes(' U ')) score += 0.1;
    if (text.includes('alstublieft') || text.includes('please')) score += 0.1;
    
    // Informal indicators
    if (this.detectProfanity(text)) score -= 0.2;
    if (this.detectEmoji(text)) score -= 0.1;
    if (text.includes('lol') || text.includes('haha')) score -= 0.1;
    if (text.includes('je ') || text.includes(' je')) score -= 0.05;
    
    return Math.max(0, Math.min(1, score));
  }

  /**
   * Estimate sentiment (positive/negative/neutral)
   */
  estimateSentiment(text) {
    const lower = text.toLowerCase();
    
    const positive = ['goed', 'great', 'perfect', 'thanks', 'dank', 'mooi', 'nice', 'love', 'geweldig', 'top', 'super'];
    const negative = ['slecht', 'bad', 'fout', 'wrong', 'niet', 'no', 'probleem', 'issue', 'kut', 'shit', 'frustrating'];
    
    const posCount = positive.filter(w => lower.includes(w)).length;
    const negCount = negative.filter(w => lower.includes(w)).length;
    
    if (posCount > negCount) return 'positive';
    if (negCount > posCount) return 'negative';
    return 'neutral';
  }

  // ==========================================
  // PROFILE UPDATES
  // ==========================================

  /**
   * Update style from observation
   */
  updateStyleFromObservation(obs) {
    // Exponential moving average for smooth updates
    const alpha = 0.1; // Learning rate
    
    this.profile.style.formality = this.profile.style.formality * (1 - alpha) + obs.formality * alpha;
    
    if (obs.hasEmoji) this.profile.style.emoji = true;
    if (obs.hasProfanity) this.profile.style.profanity = true;
    
    // Verbosity from message length
    const verbositySignal = Math.min(1, obs.wordCount / 100);
    this.profile.style.verbosity = this.profile.style.verbosity * (1 - alpha) + verbositySignal * alpha;
  }

  /**
   * Update pace from observation
   */
  updatePaceFromObservation(obs) {
    // Update average message length
    const alpha = 0.1;
    this.profile.pace.averageMessageLength = 
      this.profile.pace.averageMessageLength * (1 - alpha) + obs.length * alpha;
    
    // Detect turbo mode (many short messages quickly)
    if (this.sessionObservations.length >= 3) {
      const recent = this.sessionObservations.slice(-3);
      const avgLength = recent.reduce((sum, o) => sum + o.length, 0) / 3;
      const timeSpan = recent[recent.length - 1].timestamp - recent[0].timestamp;
      const messagesPerMin = timeSpan > 0 ? (3 / (timeSpan / 60000)) : 0;
      
      this.profile.pace.messagesPerMinute = messagesPerMin;
      this.profile.pace.prefersTurbo = messagesPerMin > 2 && avgLength < 200;
      this.profile.pace.prefersDetailed = avgLength > 300;
    }
  }

  /**
   * Update language from observation
   */
  updateLanguageFromObservation(obs) {
    if (!this.profile.language.languages.includes(obs.language)) {
      this.profile.language.languages.push(obs.language);
      this.profile.language.codeSwitch = this.profile.language.languages.length > 1;
    }
    
    // Update primary language (most recent)
    this.profile.language.primary = obs.language;
  }

  /**
   * Estimate age from patterns
   */
  estimateAgeFromPatterns() {
    // Very rough estimation based on patterns
    // NOT meant to be accurate - just for adaptation
    
    let ageSignals = [];
    
    // Emoji usage (younger users tend to use more)
    if (this.profile.style.emoji) ageSignals.push(25);
    
    // Formality (older users tend to be more formal)
    if (this.profile.style.formality > 0.7) ageSignals.push(45);
    if (this.profile.style.formality < 0.3) ageSignals.push(20);
    
    // Message length (varies less by age)
    
    // Profanity (mid-age more likely)
    if (this.profile.style.profanity) ageSignals.push(30);
    
    // Calculate average if we have signals
    if (ageSignals.length > 0) {
      this.profile.estimatedAge = Math.round(ageSignals.reduce((a, b) => a + b) / ageSignals.length);
      this.profile.ageConfidence = Math.min(0.5, ageSignals.length * 0.15);
    }
  }

  /**
   * Detect current state from patterns
   */
  detectStateFromPatterns() {
    if (this.sessionObservations.length < 3) return;
    
    const recent = this.sessionObservations.slice(-5);
    
    // Rapid fire + short messages = ACTIVATED
    const avgLength = recent.reduce((sum, o) => sum + o.length, 0) / recent.length;
    const timeSpan = recent[recent.length - 1].timestamp - recent[0].timestamp;
    const msgsPerMin = timeSpan > 0 ? (recent.length / (timeSpan / 60000)) : 0;
    
    let newState = 'NORMAL';
    
    if (msgsPerMin > 3 && avgLength < 100) {
      newState = 'ACTIVATED'; // Turbo mode
    } else if (msgsPerMin > 2) {
      newState = 'HIGH';
    } else if (avgLength > 500) {
      newState = 'BLOCKED'; // Long messages = possibly trying to explain blocked thoughts
    }
    
    // Detect negative sentiment = possible LOW
    const recentSentiments = recent.map(o => o.sentiment);
    const negativeCount = recentSentiments.filter(s => s === 'negative').length;
    if (negativeCount >= 2) {
      newState = 'LOW';
    }
    
    // Update state
    if (newState !== this.profile.states.currentState) {
      this.profile.states.stateHistory.push({
        from: this.profile.states.currentState,
        to: newState,
        timestamp: Date.now()
      });
      this.profile.states.currentState = newState;
      this.profile.states.lastStateChange = Date.now();
      
      // Keep only last 20 state changes
      if (this.profile.states.stateHistory.length > 20) {
        this.profile.states.stateHistory = this.profile.states.stateHistory.slice(-20);
      }
    }
  }

  /**
   * Update confidence level
   */
  updateConfidence() {
    // Confidence increases with more data
    const messageBonus = Math.min(0.5, this.profile.totalMessages / 100);
    const observationBonus = Math.min(0.3, this.sessionObservations.length / 20);
    
    this.profile.confidenceLevel = Math.min(1, messageBonus + observationBonus);
  }

  // ==========================================
  // ADAPTATION OUTPUT
  // ==========================================

  /**
   * Get adaptation instructions for AI prompt
   * Combines style/language adaptation with full spectrum state detection
   */
  getAdaptationInstructions() {
    const p = this.profile;
    let instructions = [];
    let safetyFlags = [];
    
    // Only add instructions if we're confident enough
    if (p.confidenceLevel < 0.2 && p.totalMessages < 5) {
      return '[User profile still building - use neutral defaults, ask "Bedoel je dat?" if unclear]';
    }
    
    // ==== STYLE & LANGUAGE ADAPTATION ====
    
    // Language
    if (p.language.primary === 'nl') {
      instructions.push('Respond in Dutch unless user switches');
    } else if (p.language.primary === 'fa') {
      instructions.push('User understands Farsi - can use when culturally appropriate');
    }
    if (p.language.codeSwitch) {
      instructions.push('User code-switches - match their language');
    }
    
    // Style
    if (p.style.formality < 0.3) {
      instructions.push('Casual tone');
    } else if (p.style.formality > 0.7) {
      instructions.push('Professional tone');
    }
    
    if (p.style.profanity) {
      instructions.push('Match profanity level');
    }
    
    if (p.style.verbosity < 0.3) {
      instructions.push('SHORT responses');
    } else if (p.style.verbosity > 0.7) {
      instructions.push('Detailed responses OK');
    }
    
    // Pace
    if (p.pace.prefersTurbo) {
      instructions.push('TURBO MODE - concise, fast');
    }
    
    // Age adaptation
    if (p.estimatedAge && p.ageConfidence > 0.3) {
      if (p.estimatedAge < 12) {
        instructions.push('Child-friendly language');
        safetyFlags.push('MINOR_USER');
      } else if (p.estimatedAge < 18) {
        instructions.push('Teen-appropriate');
        safetyFlags.push('MINOR_USER');
      } else if (p.estimatedAge > 65) {
        instructions.push('Clear structure, patient pacing');
      }
    }
    
    // ==== FULL SPECTRUM STATE DETECTION ====
    
    const stateAdaptation = stateDetection.getCurrentAdaptation();
    
    // Add state-based instructions
    if (stateAdaptation.instructions) {
      instructions.push(stateAdaptation.instructions);
    }
    
    // Add safety flags
    if (stateAdaptation.safetyFlags && stateAdaptation.safetyFlags.length > 0) {
      safetyFlags.push(...stateAdaptation.safetyFlags);
    }
    
    // ==== COMPILE OUTPUT ====
    
    let output = '';
    
    // Safety first
    if (safetyFlags.length > 0) {
      output += `[⚠️ Safety: ${safetyFlags.join(', ')}] `;
    }
    
    // Main instructions
    if (instructions.length > 0) {
      output += `[Adaptation: ${instructions.join(' | ')}]`;
    }
    
    // Add profile summary for context
    if (stateAdaptation.profile && stateAdaptation.profile.primary !== 'unknown') {
      const profile = stateAdaptation.profile;
      output += ` [Profile: ${profile.primary}${profile.traits.length > 0 ? ` (${profile.traits.join(', ')})` : ''}, confidence: ${Math.round(profile.confidence * 100)}%]`;
    }
    
    return output || null;
  }

  /**
   * Get full spectrum scores (for debugging/display)
   */
  getSpectrumScores() {
    const stateAdaptation = stateDetection.getCurrentAdaptation();
    return stateAdaptation.scores || {};
  }

  /**
   * Get safety status
   */
  getSafetyStatus() {
    const stateAdaptation = stateDetection.getCurrentAdaptation();
    return {
      flags: stateAdaptation.safetyFlags || [],
      psychosisConcern: stateAdaptation.scores?.psychosisConcern || 0,
      needsAttention: (stateAdaptation.safetyFlags?.length || 0) > 0
    };
  }

  /**
   * Get current profile summary
   */
  getProfileSummary() {
    const p = this.profile;
    const spectrum = this.getSpectrumScores();
    const stateData = stateDetection.getCurrentAdaptation();
    
    return {
      totalMessages: p.totalMessages,
      confidence: Math.round(p.confidenceLevel * 100) + '%',
      estimatedAge: p.estimatedAge,
      primaryLanguage: p.language.primary,
      currentState: p.states.currentState,
      style: {
        formality: p.style.formality < 0.4 ? 'casual' : p.style.formality > 0.6 ? 'formal' : 'neutral',
        verbosity: p.style.verbosity < 0.4 ? 'terse' : p.style.verbosity > 0.6 ? 'detailed' : 'moderate',
        usesProfanity: p.style.profanity,
        usesEmoji: p.style.emoji,
      },
      pace: p.pace.prefersTurbo ? 'turbo' : p.pace.prefersDetailed ? 'detailed' : 'normal',
      // NEW: Full spectrum data
      spectrum: {
        primary: stateData.profile?.primary || 'unknown',
        traits: stateData.profile?.traits || [],
        scores: spectrum
      },
      safety: this.getSafetyStatus()
    };
  }

  /**
   * Reset session observations (on new chat)
   */
  resetSession() {
    this.sessionObservations = [];
  }

  /**
   * Clear all data (for testing/privacy)
   */
  clearProfile() {
    localStorage.removeItem(this.storageKey);
    this.profile = this.loadProfile();
    this.sessionObservations = [];
    // Also reset state detection
    stateDetection.reset();
  }

  /**
   * Get full state detection data (for advanced debugging)
   */
  getFullStateData() {
    return stateDetection.getFullState();
  }
}

// Singleton
const userAdaptation = new UserAdaptation();

export default userAdaptation;
